import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { useLiveData } from '../../hooks/useLiveData';
import { mergeEmrSettings } from '../../config/emrSettings';
import GetStartedChecklist from '../../components/onboarding/GetStartedChecklist';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { qrDataUrl } from '../../utils/qrCode';

function QrPreview({ url, label }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!url) { setSrc(''); return undefined; }
    qrDataUrl(url, 200).then((data) => { if (!cancelled) setSrc(data); }).catch(() => { if (!cancelled) setSrc(''); });
    return () => { cancelled = true; };
  }, [url]);
  if (!url) return <p className="hint">Set a booking URL below to generate QR.</p>;
  if (!src) return <p className="hint">Generating QR…</p>;
  return (
    <div style={{ textAlign: 'center' }}>
      <img src={src} alt={`QR for ${label}`} width={200} height={200} style={{ border: '1px solid #e2e8f0', borderRadius: 8 }} />
      <p className="hint" style={{ fontSize: 11, wordBreak: 'break-all', marginTop: 8 }}>{url}</p>
    </div>
  );
}

export default function ClinicGrowthPage() {
  const { user } = useAuth();
  const { hospital, reload: reloadProfile } = useHospitalProfile();
  const { data: dir } = useHospitalDirectory();
  const { data: settingsData, reload: reloadSettings } = useLiveData(() => ipApi.getEmrSettings(), []);
  const settings = mergeEmrSettings(settingsData?.settings);

  const defaultBooking = typeof window !== 'undefined'
    ? `${window.location.origin}/reception/queue`
    : 'https://networks.hcen.in/reception/queue';

  const [form, setForm] = useState({
    googleReviewUrl: '',
    clinicBookingUrl: defaultBooking,
  });
  const [announcement, setAnnouncement] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [reviewPhone, setReviewPhone] = useState('');

  useEffect(() => {
    if (hospital) {
      setForm({
        googleReviewUrl: hospital.googleReviewUrl || '',
        clinicBookingUrl: hospital.clinicBookingUrl || defaultBooking,
      });
    }
  }, [hospital, defaultBooking]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await ipApi.patchHospitalProfile({
        googleReviewUrl: form.googleReviewUrl.trim(),
        clinicBookingUrl: form.clinicBookingUrl.trim(),
      });
      await reloadProfile();
      setMsg('Clinic links saved.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const postAnnouncement = async () => {
    if (!announcement.trim()) return;
    setBusy(true);
    try {
      const list = [
        { id: `ANN-${Date.now()}`, text: announcement.trim(), at: new Date().toISOString(), by: user?.name || 'Manager' },
        ...(settings.pushAnnouncements || []),
      ].slice(0, 20);
      await ipApi.patchEmrSettings({ pushAnnouncements: list });
      setAnnouncement('');
      await reloadSettings();
      setMsg('Announcement posted to all staff dashboards.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendReviewSms = async () => {
    if (!form.googleReviewUrl) {
      setMsg('Add Google review link first.');
      return;
    }
    const phone = String(reviewPhone || '').trim();
    if (!phone) {
      setMsg('Enter patient mobile number to send the review SMS.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const hospitalName = hospital?.name || user?.hospital || 'our hospital';
      const message = `Thank you for visiting ${hospitalName}. Please rate us: ${form.googleReviewUrl}`;
      const res = await ipApi.sendSms({
        phone,
        patientName: '',
        template: 'Feedback / Review',
        message,
        channel: 'SMS',
      });
      const entry = res?.logs?.[0];
      const status = entry?.status || 'Logged';
      if (status === 'Sent') setMsg('Review SMS sent.');
      else if (status === 'Failed') setMsg(`SMS failed: ${entry?.providerRef || 'gateway error'}.`);
      else setMsg(res?.live ? 'SMS logged.' : 'SMS logged (configure MSG91 in Settings for live delivery).');
      setReviewPhone('');
    } catch (err) {
      setMsg(err.message || 'Could not send SMS');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      title="Clinic Growth"
      subtitle="QR booking, Google reviews, and staff announcements for your clinic."
    >
      {msg && <div className="hint mb" style={{ color: 'var(--color-primary-dark)' }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <GetStartedChecklist hospital={hospital} directory={dir} emrSettings={settings} />
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Push updates to staff</h3>
          <p className="hint" style={{ fontSize: 12 }}>Broadcast to all staff on login dashboard.</p>
          <textarea className="input" rows={3} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} placeholder="e.g. New lab timings from Monday…" />
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={postAnnouncement}>Post update</button>
          {(settings.pushAnnouncements || []).slice(0, 5).map((a) => (
            <div key={a.id} className="hint" style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6, fontSize: 12 }}>
              <strong>{a.by}</strong> · {new Date(a.at).toLocaleString('en-IN')}<br />{a.text}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Clinic QR — ABDM & appointments</h3>
          <QrPreview url={form.clinicBookingUrl} label="booking" />
          <form onSubmit={saveProfile} style={{ marginTop: 12 }}>
            <div className="field full">
              <label>Self check-in / booking URL</label>
              <input className="input" value={form.clinicBookingUrl} onChange={(e) => setForm({ ...form, clinicBookingUrl: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-secondary btn-sm" disabled={busy}>Save QR link</button>
          </form>
          <p className="hint" style={{ fontSize: 11, marginTop: 8 }}>Print this QR at reception for walk-in scan-to-queue. ABDM HIP linking uses ABHA module when sandbox is live.</p>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Google review link</h3>
          <form onSubmit={saveProfile}>
            <div className="field full">
              <label>Google Maps review URL</label>
              <input className="input" value={form.googleReviewUrl} onChange={(e) => setForm({ ...form, googleReviewUrl: e.target.value })} placeholder="https://g.page/r/…/review" />
            </div>
            <div className="field full" style={{ marginTop: 8 }}>
              <label>Patient mobile (for review SMS)</label>
              <input className="input" value={reviewPhone} onChange={(e) => setReviewPhone(e.target.value)} placeholder="9876543210" />
            </div>
            <div className="row-actions" style={{ marginTop: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>Save</button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={sendReviewSms}>Send review SMS</button>
            </div>
          </form>
          <p className="hint" style={{ fontSize: 11, marginTop: 12 }}>
            After consultation, reception can SMS: &quot;Thank you for visiting {hospital?.name || user?.hospital}. Please rate us: {form.googleReviewUrl || '[link]'}&quot;
          </p>
        </div>
      </div>
    </PageShell>
  );
}
